/**
 * `/` Dashboard (§16.2): live funnel, entries by source platform, paid reconciliation, prize liability, claim
 * and fulfillment SLA, risk exceptions, unmessaged winners (§1.3 guardrail) and the §19 metric areas. Reads
 * `GET /v1/admin/dashboard` (contracts `DashboardDto`) every 30 s. Tiles are honest: a null section renders
 * "No data yet", never a zero that looks like a fact (D-1007).
 */
import { useQuery } from '@tanstack/react-query';
import { MetricArea, type DashboardDto, type MetricValueDto } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { useSession } from '../../auth/session';
import { BarsChart, TrendChart } from '../../components/Chart';
import { ErrorState, NoDataYet } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { humanizeStatus } from '../../components/StatusBadge';
import { analyticsApi } from '../../lib/api/analytics';
import { isApiError } from '../../lib/api/client';

export const DASHBOARD_REFRESH_MS = 30_000;

export function formatMetric(intl: ReturnType<typeof useIntl>, m: MetricValueDto): string {
  if (m.money) return intl.formatMoney(m.money.amountMinor, m.money.currency);
  if (m.value === null) return 'No data yet';
  if (m.unit === 'ratio') return `${intl.formatNumber(Math.round(m.value * 1000) / 10)}%`;
  if (m.unit === 'hours') return `${intl.formatNumber(m.value)} h`;
  if (m.unit === 'seconds') return `${intl.formatNumber(m.value)} s`;
  return intl.formatNumber(m.value);
}

/** §19 metric area panel with definitions and cautions beside every number. */
export function MetricAreaPanel({ area, lotteryId }: { area: MetricArea; lotteryId?: string }) {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['metrics', area, lotteryId ?? null],
    queryFn: () => analyticsApi.area(area, lotteryId ? { lotteryId } : {}),
    refetchInterval: DASHBOARD_REFRESH_MS * 2,
  });
  if (query.isError)
    return (
      <ErrorState
        title="Metrics unavailable"
        body="Retry, or open the dashboard again in a moment."
        reference={isApiError(query.error) ? query.error.supportReference : undefined}
        action={<Button onClick={() => query.refetch()}>Retry</Button>}
      />
    );
  const data = query.data;
  return (
    <section
      className="wd-surface"
      style={{ padding: 'var(--wd-space-4)' }}
      aria-busy={query.isPending || undefined}
    >
      <h3>{humanizeStatus(area)}</h3>
      {data ? <p className="wd-muted">Definition caution: {data.caution}</p> : null}
      {data ? (
        <table className="wdc-table" data-testid={`metrics-${area}`}>
          <caption className="wd-sr-only">{humanizeStatus(area)} metrics with definitions</caption>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Value</th>
              <th scope="col">Definition</th>
            </tr>
          </thead>
          <tbody>
            {data.metrics.map((m) => (
              <tr key={m.key}>
                <th scope="row">{m.label}</th>
                <td className="wd-tabular">{formatMetric(intl, m)}</td>
                <td className="wd-muted">{m.definition}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <NoDataYet what="metrics" />
      )}
    </section>
  );
}

export function DashboardPage() {
  const intl = useIntl();
  const { session } = useSession();
  const [area, setArea] = useState<MetricArea>('funnel');
  const query = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analyticsApi.dashboard(),
    refetchInterval: DASHBOARD_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const d: DashboardDto | undefined = query.data;
  const loading = query.isPending;
  const notBuilt = query.isError && isApiError(query.error) && query.error.status === 404;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          d
            ? `As of ${intl.formatDateTime(d.asOf)} · ${intl.formatNumber(d.liveLotteries)} live · refreshes every ${DASHBOARD_REFRESH_MS / 1000} s.`
            : 'Live operations across your lotteries.'
        }
        actions={
          <>
            <Button variant="secondary" href="/reports">
              Reports
            </Button>
            <Button href="/lotteries/new" variant="primary">
              Create lottery
            </Button>
          </>
        }
      />
      {query.isError && !notBuilt ? (
        <ErrorState
          title="Dashboard data is unavailable"
          body="The rest of the console still works. Retry, or open the console you need from the sidebar."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : null}
      {notBuilt ? (
        <div className="wdc-callout" data-tone="warning">
          <p>
            <strong>No dashboard data yet.</strong> <code>GET /v1/admin/dashboard</code> is not available in
            this environment; tiles show their empty state until the analytics module is registered.
          </p>
        </div>
      ) : null}
      <div className="wdc-grid">
        <StatCard
          label="Live funnel"
          loading={loading}
          value={d?.funnel ? intl.formatNumber(d.funnel.confirmedEntries) : null}
          hint={
            d?.funnel
              ? `confirmed entries · ${intl.formatNumber(d.funnel.views)} views · ${intl.formatNumber(d.funnel.entryStarts)} starts · ${intl.formatNumber(d.funnel.paymentPending)} payments pending (not entries)`
              : undefined
          }
          emptyHint="Appears once a lottery is live."
          action={
            <Link to="/lotteries" className="wd-btn" data-variant="quiet">
              Open lotteries
            </Link>
          }
        />
        <StatCard
          label="Paid reconciliation"
          loading={loading}
          value={
            d?.paidReconciliation
              ? intl.formatMoney(
                  d.paidReconciliation.collected.amountMinor,
                  d.paidReconciliation.collected.currency,
                )
              : null
          }
          hint={
            d?.paidReconciliation
              ? `${intl.formatNumber(d.paidReconciliation.succeeded)} succeeded · ${intl.formatNumber(d.paidReconciliation.pending)} pending`
              : undefined
          }
          signal={
            d?.paidReconciliation
              ? d.paidReconciliation.exceptions > 0
                ? { tone: 'error', text: `${d.paidReconciliation.exceptions} reconciliation exceptions` }
                : { tone: 'success', text: 'No exceptions' }
              : undefined
          }
          emptyHint="Appears with the first paid entry."
          action={
            <Link to="/finance" className="wd-btn" data-variant="quiet">
              Open finance
            </Link>
          }
        />
        <StatCard
          label="Prize liability"
          loading={loading}
          value={
            d?.prizeLiability
              ? intl.formatMoney(d.prizeLiability.committed.amountMinor, d.prizeLiability.committed.currency)
              : null
          }
          hint={
            d?.prizeLiability
              ? `${intl.formatNumber(d.prizeLiability.unitsReserved)} units reserved or allocated${d.prizeLiability.cap ? ` · cap ${intl.formatMoney(d.prizeLiability.cap.amountMinor, d.prizeLiability.cap.currency)}` : ''}`
              : undefined
          }
          emptyHint="Computed from prizes attached to lotteries."
        />
        <StatCard
          label="Claim SLA"
          loading={loading}
          value={d?.claimSla ? intl.formatNumber(d.claimSla.open) : null}
          hint={
            d?.claimSla
              ? `open claims · median ${d.claimSla.medianHoursToDecision === null ? '—' : `${intl.formatNumber(d.claimSla.medianHoursToDecision)} h`} to decision`
              : undefined
          }
          signal={
            d?.claimSla
              ? d.claimSla.overdue > 0
                ? { tone: 'error', text: `${d.claimSla.overdue} past one business day` }
                : { tone: 'success', text: 'Within SLA' }
              : undefined
          }
          emptyHint="Appears with the first claim."
          action={
            <Link to="/claims" className="wd-btn" data-variant="quiet">
              Open claims queue
            </Link>
          }
        />
        <StatCard
          label="Fulfillment SLA"
          loading={loading}
          value={d?.fulfillmentSla ? intl.formatNumber(d.fulfillmentSla.open) : null}
          hint={d?.fulfillmentSla ? 'open fulfillments' : undefined}
          signal={
            d?.fulfillmentSla
              ? d.fulfillmentSla.exceptions + d.fulfillmentSla.overdue > 0
                ? {
                    tone: 'warning',
                    text: `${d.fulfillmentSla.exceptions} exceptions · ${d.fulfillmentSla.overdue} overdue`,
                  }
                : { tone: 'success', text: 'On track' }
              : undefined
          }
          emptyHint="Appears once a claim is approved."
        />
        <StatCard
          label="Risk exceptions"
          loading={loading}
          value={d?.riskExceptions ? intl.formatNumber(d.riskExceptions.open) : null}
          signal={
            d?.riskExceptions
              ? d.riskExceptions.highSeverity > 0
                ? { tone: 'error', text: `${d.riskExceptions.highSeverity} high severity` }
                : { tone: 'success', text: 'None high severity' }
              : undefined
          }
          emptyHint="Reconciliation exceptions, risk flags and urgent cases."
          action={
            <Link to="/audit" className="wd-btn" data-variant="quiet">
              Open audit & incidents
            </Link>
          }
        />
        <StatCard
          label="Unmessaged winners"
          loading={loading}
          value={d?.unmessagedWinners ? intl.formatNumber(d.unmessagedWinners.count) : null}
          hint={
            d?.unmessagedWinners
              ? `${intl.formatNumber(d.unmessagedWinners.unassigned)} without an assigned admin${d.unmessagedWinners.oldestHours === null ? '' : ` · oldest ${intl.formatNumber(d.unmessagedWinners.oldestHours)} h`}`
              : undefined
          }
          signal={
            d?.unmessagedWinners
              ? d.unmessagedWinners.unassigned > 0
                ? { tone: 'warning', text: 'Assign an admin and a reminder' }
                : { tone: 'success', text: 'Every winner has an owner' }
              : undefined
          }
          emptyHint="Appears with the first winner."
          action={
            <Link to="/winners" className="wd-btn" data-variant="quiet">
              Open winners board
            </Link>
          }
        />
      </div>
      <div
        className="wdc-grid"
        style={{
          marginTop: 'var(--wd-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        }}
      >
        <TrendChart
          title="Confirmed entries"
          description="Hourly confirmed entries across live lotteries (last 24 hours)."
          data={d?.entriesSeries ?? []}
          xKey="at"
          xLabel="Hour"
          formatX={(v) => intl.formatTime(String(v))}
          series={[{ key: 'confirmedEntries', label: 'Confirmed entries' }]}
          emptyHint="The trend starts with the first confirmed entry."
        />
        <BarsChart
          title="Entries by source platform"
          description="Where confirmed participants arrived from."
          data={(d?.entriesBySource ?? []).map((r) => ({ platform: r.platform, count: r.count }))}
          xKey="platform"
          xLabel="Platform"
          series={[{ key: 'count', label: 'Entries', color: 'secondary' }]}
          emptyHint="Platforms appear once entries carry a source."
        />
      </div>
      <section style={{ marginTop: 'var(--wd-space-6)' }}>
        <h2>§19 metrics</h2>
        <ul className="wdc-tabs" role="list" aria-label="Metric areas">
          {MetricArea.options.map((a) => (
            <li key={a}>
              <a
                href={`#area-${a}`}
                className={a === area ? 'active' : undefined}
                aria-current={a === area ? 'true' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  setArea(a);
                }}
              >
                {humanizeStatus(a)}
              </a>
            </li>
          ))}
        </ul>
        <div id={`area-${area}`}>
          <MetricAreaPanel area={area} />
        </div>
      </section>
      {session?.tenantSlug ? null : (
        <p className="wd-muted" style={{ marginTop: 'var(--wd-space-4)' }}>
          You are signed in without a tenant; platform consoles live under Settings.
        </p>
      )}
    </>
  );
}
